import React, { useEffect, useState } from 'react'
import TitleHead from '../../components/Customs/TitleHead'
import { Card, Table, TimePicker, Button, InputNumber, Upload, Image, Space, Empty } from 'antd'
import { SaveOutlined, UploadOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { RoomServices } from '../../Services/RoomServices'
import { notify } from '../../components/Customs/NotifyProvider'
import { GeneralService } from '../../Services/GeneralService'
import { ENDPOINTS } from '../../Services/ENDPOINT'

import { canViewModule, canCreateModule, canUpdateModule, canDeleteModule } from '../../../utils/permissionUtils';

const MODULE_KEY = "settings";



const Settings = () => {
    const [values, setValues] = useState({})
    const [originalValues, setOriginalValues] = useState({})
    const [changedRows, setChangedRows] = useState({})

    const getDefaultVal = async () => {
        try {
            const req = await RoomServices.getAllByTable('utills')

            const defaultData = {
                checkin: req.data[0].checkin_time
                    ? dayjs(req.data[0].checkin_time, 'hh:mm A')
                    : null,

                checkout: req.data[0].checkout_time
                    ? dayjs(req.data[0].checkout_time, 'hh:mm A')
                    : null,

                gst: req.data[0].gst ?? '',

                header_img: req.data[0].header_img
                    ? `${ENDPOINTS.IMG_HEADER}${req.data[0].header_img}`
                    : '',

                footer_img: req.data[0].footer_img
                    ? `${ENDPOINTS.IMG_HEADER}${req.data[0].footer_img}`
                    : '',

                a4_pdf_header_img: req.data[0].a4_pdf_header_img
                    ? `${ENDPOINTS.IMG_HEADER}${req.data[0].a4_pdf_header_img}`
                    : '',
            }

            setValues(defaultData)
            setOriginalValues(defaultData)
        } catch (error) {
            console.error(error)
        }
    }

    useEffect(() => {
        getDefaultVal()
    }, [])

    const handleChange = (key, time) => {
        setValues(prev => ({ ...prev, [key]: time }))

        const currentValue = time?.format('hh:mm A')
        const originalValue = originalValues?.[key]?.format('hh:mm A')

        setChangedRows(prev => ({
            ...prev,
            [key]: currentValue !== originalValue,
        }))
    }

    const handleImageUpload = async (file, keyName) => {
        try {
            const formData = new FormData()
            formData.append('image', file)

            const endpoint =
                keyName === 'header_img'
                    ? ENDPOINTS.HEADER_UPLOAD
                    : keyName === 'footer_img'
                        ? ENDPOINTS.FOOTER_UPLOAD
                        : ENDPOINTS.A4_PDF_HEADER_UPLOAD

            const res = await fetch(endpoint, {
                method: 'POST',
                body: formData,
            })

            const req = await res.json()

            if (req.success) {
                const uploadedImage = req.data?.image || req.data?.url || req.data

                setValues(prev => ({
                    ...prev,
                    [keyName]: uploadedImage,
                }))

                setChangedRows(prev => ({
                    ...prev,
                    [keyName]: true,
                }))
                getDefaultVal()

                notify.success('Image uploaded successfully')
            } else {
                getDefaultVal()
                notify.error('Image upload failed', req.message)
            }
        } catch (error) {
            console.error(error)
            notify.error('Unexpected error occurred while uploading image')
        }

        return false
    }

    const handleRowSave = async (record) => {
        try {
            const key = record.keyName

            const updatedValue =
                key === 'gst'
                    ? values.gst
                    : key === 'header_img' ||
                        key === 'footer_img' ||
                        key === 'a4_pdf_header_img'
                        ? values[key]
                        : values?.[key]?.format('hh:mm A')

            // const payload = {
            //     [key === 'checkin'
            //         ? 'checkin_time'
            //         : key === 'checkout'
            //             ? 'checkout_time'
            //             : key]: updatedValue,
            // }

            const payload = {
                checkin_time: values?.checkin
                    ? values.checkin.format("hh:mm A")
                    : "",

                checkout_time: values?.checkout
                    ? values.checkout.format("hh:mm A")
                    : "",

                gst: values?.gst ?? "",
            };

            const req = await GeneralService.update('utills', 6, payload)

            if (req.status) {
                notify.success('Settings updated')

                setOriginalValues(prev => ({
                    ...prev,
                    [key]: values[key],
                }))

                setChangedRows(prev => ({
                    ...prev,
                    [key]: false,
                }))
            } else {
                notify.error('Something went wrong!', req.message)
            }
        } catch (error) {
            console.error(error)
            notify.error('Unexpected error occurred')
        }
    }

    const columns = [
        {
            title: 'S.No',
            key: 'sno',
            align: 'center',
            render: (_, __, index) => index + 1,
        },
        {
            title: 'Setting',
            dataIndex: 'label',
            key: 'label',
        },
        {
            title: 'Value',
            key: 'value',
            render: (_, record) => {
                if (record.keyName === 'gst') {
                    return (
                        <InputNumber
                            value={values.gst}
                            min={0}
                            max={100}
                            formatter={(value) => `${value}%`}
                            parser={(value) => value.replace('%', '')}
                            onChange={(val) => {
                                setValues(prev => ({ ...prev, gst: val }))
                                setChangedRows(prev => ({
                                    ...prev,
                                    gst: val !== originalValues?.gst,
                                }))
                            }}
                        />
                    )
                }

                if (
                    record.keyName === 'header_img' ||
                    record.keyName === 'footer_img' ||
                    record.keyName === 'a4_pdf_header_img'
                ) {
                    return (
                        <Space>
                            {values[record.keyName] && (
                                <Image
                                    src={values[record.keyName]}
                                    width={120}
                                    fallback="https://placehold.co/120x50?text=Image+Not+Found"
                                    height={50}
                                    style={{ objectFit: 'contain' }}
                                />
                            )}

                        </Space>
                    )
                }

                return (
                    <TimePicker
                        value={values[record.keyName]}
                        format="hh:mm A"
                        use12Hours
                        onChange={(time) => handleChange(record.keyName, time)}
                    />
                )
            },
        },
        {
            title: 'Action',
            key: 'action',
            align: 'center',
            render: (_, record) => {
                // IMAGE UPLOAD ACTION
                if (
                    record.keyName === 'header_img' ||
                    record.keyName === 'footer_img' ||
                    record.keyName === 'a4_pdf_header_img'
                ) {
                    return (
                        <Space>


                            <Upload
                                showUploadList={false}
                                beforeUpload={(file) =>
                                    handleImageUpload(file, record.keyName)
                                }
                                accept="image/*"
                                disabled={!canUpdateModule(MODULE_KEY)}
                            >
                                <Button icon={<UploadOutlined />} disabled={!canUpdateModule(MODULE_KEY)}>
                                    Upload
                                </Button>
                            </Upload>


                        </Space>
                    )
                }

                // NORMAL SAVE BUTTON
                return (
                    <Button
                        type="primary"
                        icon={<SaveOutlined />}
                        disabled={!changedRows[record.keyName] || !canUpdateModule(MODULE_KEY)}
                        onClick={() => handleRowSave(record)}
                    >
                        Save
                    </Button>
                )
            },
        },
    ]

    const data = [
        {
            key: '1',
            label: 'Standard Checkin Time',
            keyName: 'checkin',
        },
        {
            key: '2',
            label: 'Standard Checkout Time',
            keyName: 'checkout',
        },
        {
            key: '3',
            label: 'GST (in %)',
            keyName: 'gst',
        },
        {
            key: '4',
            label: 'Upload PDF Header',
            keyName: 'header_img',
        },
        {
            key: '5',
            label: 'Upload PDF Footer',
            keyName: 'footer_img',
        },
        {
            key: '6',
            label: 'Upload A4 PDF Header Image',
            keyName: 'a4_pdf_header_img',
        },
    ]
    if (!canViewModule(MODULE_KEY)) {
        return <Empty description="No permission to view this page" />;
    }

    return (
        <Card
            title={
                <TitleHead
                    h1="Settings"
                    h2="Manage checkin and checkout time and other activity overview."
                />
            }
        >
            <Table
                columns={columns}
                dataSource={data}
                pagination={false}
                bordered
            />
        </Card>
    )
}

export default Settings